import type { App } from "vue";
import { isPwaRuntimeEligible } from "@/services/pwaEligibility";
import { setupPushNotificationLifecycle } from "@/services/pushNotifications";
import { isStandaloneDisplay } from "@/utils/nativeAppMode";

const PWA_TOAST_ID = "alkokh-pwa-toast";
const SERVICE_WORKER_URL = "/alkokh-sw.js";
const INSTALL_DISMISSED_KEY = "alkokh-pwa-install-dismissed";
const UPDATE_RELOAD_KEY = "alkokh-pwa-update-reload-at";
const UPDATE_RELOAD_GRACE_MS = 60_000;

// Minimal, paired en/ar copy for the install affordance. pwa.ts runs at page
// `load` (outside the Vue/i18n app context), so we resolve the locale from the
// same `language` cookie the app uses rather than pulling in the i18n instance.
const INSTALL_STRINGS = {
  en: {
    prompt: "Install Alkokh on your device for a full-screen app experience.",
    install: "Install",
    dismiss: "Not now",
    iosHint: "To install: tap the Share button, then “Add to Home Screen”.",
    iosDismiss: "Got it",
    updateReady: "A new version is ready.",
    reload: "Reload",
  },
  ar: {
    prompt: "ثبّت الكوخ على جهازك لتجربة تطبيق بملء الشاشة.",
    install: "تثبيت",
    dismiss: "لاحقاً",
    iosHint: "للتثبيت: اضغط زر المشاركة ثم “إضافة إلى الشاشة الرئيسية”.",
    iosDismiss: "حسناً",
    updateReady: "يتوفر إصدار جديد.",
    reload: "إعادة التحميل",
  },
} as const;

function currentPwaLocale() {
  const match = document.cookie.match(/(?:^|;\s*)language=([^;]+)/);
  const locale = match ? decodeURIComponent(match[1]) : "";

  return locale.startsWith("ar") ? "ar" : "en";
}

function pwaStrings() {
  return INSTALL_STRINGS[currentPwaLocale()];
}

function dismissToast() {
  document.getElementById(PWA_TOAST_ID)?.remove();
}

interface ToastAction {
  label: string;
  onClick: () => void;
  variant?: "primary" | "ghost";
}

function toastButton({ label, onClick, variant = "primary" }: ToastAction) {
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = label;
  button.style.border = "0";
  button.style.borderRadius = "6px";
  button.style.padding = "8px 12px";
  button.style.background = variant === "primary" ? "#7367f0" : "transparent";
  button.style.color = "#fff";
  button.style.font = "inherit";
  button.style.cursor = "pointer";
  if (variant === "ghost")
    button.style.opacity = "0.8";
  button.addEventListener("click", onClick);

  return button;
}

function showPwaToast(message: string, actions: ToastAction[] = []) {
  dismissToast();

  const toast = document.createElement("div");
  toast.id = PWA_TOAST_ID;
  toast.setAttribute("role", "status");
  toast.style.position = "fixed";
  toast.style.insetInline = "16px";
  toast.style.insetBlockEnd = "calc(16px + env(safe-area-inset-bottom, 0px))";
  toast.style.zIndex = "2147483647";
  toast.style.display = "flex";
  toast.style.alignItems = "center";
  toast.style.justifyContent = "space-between";
  toast.style.gap = "12px";
  toast.style.maxWidth = "560px";
  toast.style.marginInline = "auto";
  toast.style.padding = "12px 14px";
  toast.style.borderRadius = "8px";
  toast.style.boxShadow = "0 10px 32px rgba(15, 23, 42, 0.22)";
  toast.style.background = "#1f2937";
  toast.style.color = "#fff";
  toast.style.font = "500 14px/1.4 system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif";

  const text = document.createElement("span");
  text.textContent = message;
  toast.append(text);

  if (actions.length) {
    const buttons = document.createElement("div");
    buttons.style.display = "flex";
    buttons.style.flexShrink = "0";
    buttons.style.gap = "6px";
    for (const action of actions)
      buttons.append(toastButton(action));
    toast.append(buttons);
  }

  document.body.append(toast);
}

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

const isStandalone = isStandaloneDisplay;

function installDismissed() {
  try {
    return localStorage.getItem(INSTALL_DISMISSED_KEY) === "1";
  }
  catch {
    return false;
  }
}

function rememberInstallDismissed() {
  try {
    localStorage.setItem(INSTALL_DISMISSED_KEY, "1");
  }
  catch {
    // localStorage may be unavailable (private mode) — ignore.
  }
}

// The `beforeinstallprompt` event is not yet in the DOM lib types.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function setupInstallAffordance() {
  // Already installed, or the user dismissed the prompt before — stay quiet.
  if (isStandalone() || installDismissed())
    return;

  const strings = pwaStrings();

  // Android / desktop Chromium: capture the deferred prompt and offer a button.
  window.addEventListener("beforeinstallprompt", event => {
    event.preventDefault();
    const deferred = event as BeforeInstallPromptEvent;

    showPwaToast(strings.prompt, [
      {
        label: strings.dismiss,
        variant: "ghost",
        onClick: () => {
          rememberInstallDismissed();
          dismissToast();
        },
      },
      {
        label: strings.install,
        onClick: () => {
          dismissToast();
          void deferred.prompt();
        },
      },
    ]);
  });

  window.addEventListener("appinstalled", () => {
    rememberInstallDismissed();
    dismissToast();
  });

  // iOS Safari has no beforeinstallprompt — show a one-time Share-sheet hint.
  if (isIos()) {
    showPwaToast(strings.iosHint, [
      {
        label: strings.iosDismiss,
        onClick: () => {
          rememberInstallDismissed();
          dismissToast();
        },
      },
    ]);
  }
}

// The worker calls `skipWaiting()` on install, so it activates and starts
// serving the corrected fetch handler on its own. A reload is NOT needed for
// that fix to take effect — it only refreshes the HTML/JS to the newer build.
// Since a tab may be a doctor part-way through a visit or a cashier part-way
// through an invoice, and the app has no `beforeunload` guard and no draft
// persistence, we reload strictly when the user asks for it.
let reloadRequested = false;
let reloading = false;
const promptedWorkers = new WeakSet<ServiceWorker>();

function rememberUpdateReload() {
  try {
    sessionStorage.setItem(UPDATE_RELOAD_KEY, String(Date.now()));
  }
  catch {
    // sessionStorage may be unavailable — the normal worker lifecycle still
    // completes, only the duplicate-prompt grace period is lost.
  }
}

function recentlyReloadedForUpdate() {
  try {
    const reloadedAt = Number(sessionStorage.getItem(UPDATE_RELOAD_KEY) ?? 0);
    const recent = Number.isFinite(reloadedAt)
      && reloadedAt > 0
      && Date.now() - reloadedAt < UPDATE_RELOAD_GRACE_MS;

    if (!recent)
      sessionStorage.removeItem(UPDATE_RELOAD_KEY);

    return recent;
  }
  catch {
    return false;
  }
}

function reloadOnce() {
  if (reloading)
    return;

  reloading = true;
  window.location.reload();
}

function promptForUpdate(worker: ServiceWorker) {
  if (promptedWorkers.has(worker))
    return;

  promptedWorkers.add(worker);

  // The same waiting/installing worker can be rediscovered while the reload
  // requested for it is settling. Do not recreate the banner immediately on
  // the freshly loaded page.
  if (recentlyReloadedForUpdate()) {
    dismissToast();
    return;
  }

  const strings = pwaStrings();

  showPwaToast(strings.updateReady, [
    {
      label: strings.dismiss,
      variant: "ghost",
      onClick: dismissToast,
    },
    {
      label: strings.reload,
      onClick: () => {
        reloadRequested = true;
        rememberUpdateReload();
        dismissToast();
        // Harmless if the worker already activated via its own `skipWaiting`.
        worker.postMessage({ type: "SKIP_WAITING" });
        // Normally `controllerchange` reloads us. If the worker was already in
        // control there is no further controller change to wait for, so fall
        // back after a beat rather than leaving the button looking dead.
        window.setTimeout(reloadOnce, 2000);
      },
    },
  ]);
}

export default function (_app: App) {
  if (!isPwaRuntimeEligible())
    return;

  setupPushNotificationLifecycle();

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(SERVICE_WORKER_URL)
      .then(registration => {
        // A worker that installed on an earlier page load is already parked in
        // `waiting`, and `updatefound` will never fire again for it. Without
        // this check that update stays invisible forever.
        if (registration.waiting && navigator.serviceWorker.controller)
          promptForUpdate(registration.waiting);

        registration.addEventListener("updatefound", () => {
          const nextWorker = registration.installing;

          if (!nextWorker)
            return;

          nextWorker.addEventListener("statechange", () => {
            if (nextWorker.state !== "installed" || !navigator.serviceWorker.controller)
              return;

            promptForUpdate(nextWorker);
          });
        });
      })
      .catch(error => {
        console.warn("PWA service worker registration failed", error);
      });
  });

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // Fires whenever a new worker takes control — including a background
    // upgrade, and including `clients.claim()` reaching THIS tab because a
    // different tab triggered the install. Reloading here unprompted would
    // discard unsaved clinical input, so the fixed worker simply takes over
    // silently and this tab keeps running until the user opts in.
    if (!reloadRequested)
      return;

    reloadOnce();
  });

  setupInstallAffordance();
}
